import Chat, { Bubble, useMessages } from '@chatui/core';
import { MessageContainerHandle } from '@chatui/core/lib/components/MessageContainer';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { trpc } from '@tencent/tpro_rmonitor_ai_server';
import { useLocalStorageState } from 'ahooks';
import {  Drawer, Select } from 'antd';
import TextArea, { TextAreaRef } from 'antd/lib/input/TextArea';
import { get } from 'lodash-es';
import React, {  Fragment,  useEffect, useRef, useState } from 'react';
import * as uuid from 'uuid';
// 引入样式
import '@chatui/core/dist/index.css';
import BuglyCopilotIcon from '@/assets/images/header/bugly-copilot-icon.svg?react';
import { userImplement } from '@/config/hooks/user';
import { getUserId } from '@/infrastructure/auth';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { openXmMeter } from '@/infrastructure/reporter/xmMeter';
import { useOperationConfig } from '@/presentation/hooks';
import { useStores } from '@/presentation/store';
import { genStandardToken } from '@/proto/token';
import { aiCopilotServer } from '@/service/ai-copilot';
import { WithTypeCast } from '@raft/utils';
import { TopMenuButton } from '../button';
import HistoryModal from './historyModal';
import DrawerClose from './icons/drawer-close.react.svg?react';
import HeaderIcon from './icons/header-icon.react.svg?react';
import SendBtnIcon from './icons/send.react.svg?react';
import style from './index.module.scss';

import { LoadingMessage, RecommandMessage, ResponseTextMessage, TimestampMessage, HistoryRspTextMessage } from './message';

type IMessage = WithTypeCast<trpc.rmonitor.ai_server.IMessage[]>;
type HunyuanMessage = {
  role: 'user' | 'assistant',
  content: string;
};

export const BuglyCopilot = () => {
  const [sessionID, setSessionID] = useState(uuid.v4());
  const [visible, setVisible] = useState(false);
  const [block, setBlock] = useState<boolean>(false);
  const [firstQA, setFirstQA] = useState(false);
  // 某条会话包含的历史记录
  const [historyQA, setHistoryQA] = useState<IMessage>();
  const [initRecId] = useState('initRecId');
  const firstReply = useRef(false);
  const { isSuperUser } = useStores().ProductStore;
  const [model, setModel] = useLocalStorageState('bugly-copilot-model', {
    defaultValue: 'deepseek-v3-local-II',
  });

  // 获取模型配置
  const { data: modelOptions = [] } = useOperationConfig<Array<{ label: string; value: string }>>({
    configName: 'copilot-models',
  });

  // 用来提交给对话模型的对话上下文
  const [gptMessages, setGPTMessages] = useState<HunyuanMessage[]>([]);

  // 当前处理中的消息ID
  const currentMessageRef = useRef<string>('');

  // 控制显示重新生成按钮的状态
  const [enableReGen, setEnableRegen] = useState(true);

  // SSE 连接控制器
  const abortControllerRef = useRef<AbortController | null>(null);

  const [composerStyle, setComposerStyle] = useState({
    cursor: '',
    caretColor: '',
    placeholder: '',
    disabled: false,
    overflow: 'hidden',
  });

  const textAreaRef = useRef <TextAreaRef>(null);
  const sendBtnRef = useRef<HTMLDivElement | null>(null);
  const messageListRef = useRef<any>([]);
  const messagesRef = useRef<MessageContainerHandle>(null);
  const productId = getProductId();

  const { messages, appendMsg, updateMsg, resetList } = useMessages([{
    _id: initRecId,
    type: 'text',
    content: '',
    position: 'left',
  }]);
  const onClick = () => {
    setVisible(true);

    reportClick(PAGE_ID.copilot, CUSTOM_ACTIONS.commonCopilotClick, {
      from: !productId ? `${PAGE_ID.copilot}-no-product-id` : `${PAGE_ID.copilot}-has-product-id`,
    });
  };

  function renderMessageContent(msg: any) {
    const { type, content } = msg;
    switch (type) {
      case 'text':
        return (
          <Bubble >
            {content}
          </Bubble>
        );
      case 'node':
        return (
          content
        );
      case 'icon':
        return (
          <Bubble >
            {content}
          </Bubble>
        );
      default:
        return (
          content
        );
    }
  }

  const getChatMessages = async (gptMsgs: HunyuanMessage[], val: string, messageId?: string) => {
    // 用于组装流式数据
    let textStream = '';
    let thinkingStream = '';
    let queryId = '';
    let docList: any = [];

    // 每一条消息重新生成一个消息ID
    currentMessageRef.current = messageId || uuid.v4();
    const tokenFrom = gptMsgs ? { message: gptMsgs } : {  message: [] };
    const params = await genStandardToken(tokenFrom);

    // 插入一条loading消息
    !messageId ? appendMsg({
      _id: currentMessageRef.current,
      type: 'icon',
      content: <LoadingMessage />,
      position: 'left',
    }) : updateMsg(currentMessageRef.current, {
      type: 'icon',
      content: <LoadingMessage />,
      position: 'left',
    });
    updateMsgList();

    // 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // 获取 buglyToken
    const buglyToken = userImplement.getBuglyToken?.() || '';

    try {
      await fetchEventSource(`/${API_PREFIX}/api/chat/streamCopilot?param=${params}&query=${val}&userId=${getUserId()}&sessionID=${sessionID}&model=${model}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/event-stream',
          Authorization: buglyToken,
        },
        openWhenHidden: true,
        signal: controller.signal,
        onmessage(event: any) {
          let sourceData;
          if (!event.data) return;

          try {
            sourceData = JSON.parse(event.data.replace('data:', '').trim());
          } catch (error) {
            console.log('JSONParse error', error);
            return;
          }

          // 取得后台返回的消息queryID
          if (get(sourceData, 'event_type') === 'QUERYID') {
            queryId = get(sourceData, 'event_value');
          }

          // 思考过程消息
          if (get(sourceData, 'event_type') === 'THINKING') {
            const thinkingText = get(sourceData, 'event_value', '');
            thinkingStream += thinkingText;
            // 更新消息，显示思考过程
            updateMsg(currentMessageRef.current, {
              type: 'responseText',
              content: <ResponseTextMessage
                enableRegen={enableReGen}
                question={val}
                messageId={currentMessageRef.current}
                onReGen={onReGen}
                message={{
                  content: '',
                  queryId,
                  docList: [],
                  thinkingContent: thinkingStream,
                }}
              />,
              position: 'left',
            });
          }

          // 普通文本消息结构体
          if (get(sourceData, 'event_type') === 'RESULT') {
            const message = get(sourceData, 'event_value', '');
            textStream += message;
            // 更新消息，包含思考过程和回答内容
            updateMsg(currentMessageRef.current, {
              type: 'responseText',
              content: <ResponseTextMessage
                enableRegen={enableReGen}
                question={val}
                messageId={currentMessageRef.current}
                onReGen={onReGen}
                message={{
                  content: textStream,
                  queryId,
                  docList: [],
                  thinkingContent: thinkingStream,
                }}
              />,
              position: 'left',
            });
          }

          // 引用资料消息体
          if (get(sourceData, 'event_type') === 'DOCLIST') {
            docList = get(sourceData, 'event_value');
          }

          // 流式数据响应完
          if (get(sourceData, 'event_type') === 'END') {
            updateMsg(currentMessageRef.current, {
              type: 'responseText',
              content: <ResponseTextMessage
                enableRegen={enableReGen}
                finished
                showLike
                question={val}
                onReGen={onReGen}
                messageId={currentMessageRef.current}
                message={{
                  content: textStream,
                  queryId,
                  docList,
                  thinkingContent: thinkingStream,
                }}
              />,
            });

            setBlock(false);

            setGPTMessages([...gptMsgs as HunyuanMessage[], {
              role: 'assistant',
              content: textStream,
            }]);

            if (!firstReply.current) {
              firstReply.current = true;
              openXmMeter('ip1yry2frrxxxk22pn1l');
            }
          }
        },
        async onopen() {
          // 连接建立时的回调
          console.log('SSE connection opened');
        },
        onerror(error) {
          console.log('SSE error', error);
          controller.abort();
          setBlock(false);
          throw error; // 重新抛出错误以停止重连
        },
      });
    } catch (error) {
      console.error('fetchEventSource error:', error);
      setBlock(false);
    }
  };

  const onClickPrompt = (text: string) => {
    !block && handleSend('text', text);
  };

  // 初始化推荐问题
  const initRecommendedList = async () => {
    const resp = await aiCopilotServer.getPrompts({ limit: 3 });
    const prompts = resp.querys ?? [];
    updateMsg(initRecId, {
      type: 'text',
      content: <RecommandMessage
        prompts={prompts}
        onClickPrompt={onClickPrompt}
        />,
      position: 'left',
    });
  };

  // 重新发送消息，只需要根据消息ID重新走一遍发送消息流程
  const onReGen = (messageId: string, question: string) => {
    handleSend('text', question, messageId);
  };

  /**
   * 处理发送消息逻辑
   * @param messageType
   * @param value
   * @param messageId 有表示是重新生成的
   */
  const handleSend = (messageType: string, value: string, messageId?: string) => {
    setEnableRegen(true);
    // 如果是第一条提问，插入一条时间消息
    if (firstQA && !messageId) {
      appendMsg({
        type: messageType,
        content: <TimestampMessage />,
        position: 'center',
      });

      setFirstQA(false);
    }

    !messageId && appendMsg({
      type: messageType,
      content: value,
      position: 'right',
    });

    setBlock(true);
    // 开始请求大模型数据
    // 如果是新消息，将用户当前消息添加到上下文中；如果是重新生成，直接使用历史上下文
    const contexts = messageId
      ? gptMessages.slice(-10)
      : [...gptMessages.slice(-10), {
        role: 'user' as const,
        content: value,
      }];
    getChatMessages(contexts, value, messageId);
  };

  /**
   * 更新消息的【重新发送】展示状态
   */
  const updateMsgList = () => {
    messageListRef.current.forEach((msg: any) => {
      if (visible) {
        if (msg.type === 'responseText' && msg._id !== currentMessageRef.current) {
          updateMsg(msg._id, {
            type: 'responseText',
            content: <ResponseTextMessage
              enableRegen={enableReGen}
              question={msg.content?.props?.question}
              showLike
              onReGen={onReGen}
              messageId={`${msg._id}`}
              message={{
                content: msg.content?.props?.message?.content,
                queryId: msg.content?.props?.message?.queryId,
                docList: msg.content?.props?.message?.docList,
                thinkingContent: msg.content?.props?.message?.thinkingContent,
              }}
            />,
          });
        }
      } else {
        if (msg.type === 'responseText') {
          updateMsg(msg._id, {
            type: 'responseText',
            content: <ResponseTextMessage
              enableRegen={enableReGen}
              question={msg.content?.props?.question}
              showLike
              onReGen={onReGen}
              messageId={`${msg._id}`}
              message={{
                content: msg.content?.props?.message?.content,
                queryId: msg.content?.props?.message?.queryId,
                docList: msg.content?.props?.message?.docList,
                thinkingContent: msg.content?.props?.message?.thinkingContent,
              }}
            />,
          });
        }
      }
    });
  };


  useEffect(() => {
    if (block) {
      setComposerStyle({
        cursor: 'no-drop',
        caretColor: 'transparent',
        placeholder: '回复中，请稍后输入',
        disabled: true,
        overflow: 'hidden',
      });
    } else {
      setComposerStyle({
        cursor: 'text',
        caretColor: '#ff9000',
        placeholder: '请输入您的问题',
        disabled: false,
        overflow: 'hidden',
      });
    }
  }, [block]);

  useEffect(() => {
    messageListRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (historyQA) {
      resetList();
      historyQA.forEach((item) => {
        if (item.role === 0) {
          appendMsg({
            type: 'text',
            content: item.content,
            position: 'right',
          });
        } else {
          appendMsg({
            type: 'text',
            content: <HistoryRspTextMessage
              content={item.content}
              docs={item.docs}
            />,
            position: 'left',
          });
        }
      });
    }
  }, [historyQA]);

  useEffect(() => {
    if (visible) {
      setEnableRegen(true);
      setFirstQA(true);
      setSessionID(uuid.v4());
    } else {
      // 关闭抽屉时中止正在进行的 SSE 连接
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // 关闭后更新下重新发送状态
      updateMsgList();
    }
  }, [visible]);

  // 组件卸载时清理
  useEffect(() => () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  useEffect(() => {
    if (visible) {
      initRecommendedList();
    }
  }, [firstQA, block]);

  function MyComponent() {
    const [inputValue, setInputvalue] = useState('');
    const sendMsg = (e: any) => {
      if (!(e.key === 'Enter' && e.shiftKey)) {
        e.preventDefault();
        handleSend('text', inputValue);
      }
    };
    const inputChange = (e: any) => {
      setInputvalue(e.target.value);
    };

    return (
      <div className={style.composer}>
        <div className={style.inputWrap}>
          <TextArea
            style={composerStyle}
            className={style.text}
            placeholder={composerStyle.placeholder}
            value={inputValue}
            autoFocus={true}
            onPressEnter={sendMsg}
            onChange={inputChange}
            ref={textAreaRef}
          />
          <div onClick={sendMsg} style={{ cursor: !block ? 'pointer' : 'not-allowed', height: '36px', position: 'relative' }} ref={sendBtnRef}>
            <SendBtnIcon/>
          </div>
        </div>
      </div>
    );
  }


  return (
    <Fragment >
      <Drawer
        className={style.drawer}
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BuglyCopilotIcon/>
          <span>Bugly Copilot</span>
          {isSuperUser && <Select
            style={{ width: 160 }}
            dropdownMatchSelectWidth={200}
            value={model}
            onChange={setModel}
            options={modelOptions}
          />}
        </div>}
        visible={visible}
        width={480}
        closeIcon={null}
        extra={<div style={{ display: 'flex' }}>
          <HistoryModal getHistoryDetail={(historyQA: IMessage) => setHistoryQA(historyQA)}/>
          <span onClick={() => {
            setVisible(false);
            setEnableRegen(false);
          }} style={{ marginLeft: '10px', cursor: 'pointer' }}>
            <DrawerClose/>
          </span>
          </div>
        }
        mask={false}
      >
        <Chat
          messages={messages}
          renderMessageContent={renderMessageContent}
          onSend={handleSend}
          Composer={MyComponent}
          messagesRef={messagesRef}
        />
      </Drawer>
      <TopMenuButton  onClick={onClick} >
        <HeaderIcon />
        <span style={{ fontFamily: 'PingFang SC' }}>Bugly Copilot</span>
      </TopMenuButton>
    </Fragment>
  );
};
